// Colour parsing and contrast helpers for styleLoader.

export type Rgb = number[];

// Parse #rgb / #rrggbb / rgb()/rgba() to [r,g,b] (0-255), or null if unknown.
export function parseColor(value: unknown): Rgb | null {
  if (typeof value !== "string") {
    return null;
  }
  const v = value.trim();
  const hex = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  const rgb = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return [parseInt(rgb[1], 10), parseInt(rgb[2], 10), parseInt(rgb[3], 10)];
  }
  return null;
}

// A legible text colour for content sitting on `bg`: near-black on a light
// fill, white on a dark one. WCAG relative luminance with the standard
// white-vs-black crossover (~0.179). Unknown formats keep the light-fill
// default so branded-but-unparseable never lands on white-on-white.
export function readableOn(bg: string): string {
  const rgb = parseColor(bg);
  if (!rgb) {
    return "#111111";
  }
  const lin = rgb.map(function (channel) {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  return luminance > 0.179 ? "#111111" : "#ffffff";
}

// The default Pixie accent, read from the `--pixie-default-accent` custom
// property the CSS exposes -- so the contrast guard checks against the SAME
// violet the CSS paints (single source of truth, no drift). Falls back to the
// literal only when the stylesheet has not resolved the var (non-browser/test
// contexts).
export function readPixieDefaultAccent(document: Document): string {
  try {
    const view =
      document.defaultView || (typeof window !== "undefined" ? window : null);
    if (view) {
      const value = view
        .getComputedStyle(document.documentElement)
        .getPropertyValue("--pixie-default-accent");
      if (value && value.trim()) {
        return value.trim();
      }
    }
  } catch {
    // fall through to the literal below
  }
  return "#7c3aed";
}

// Resolve any CSS colour string to [r,g,b]. #hex and rgb()/rgba() parse
// directly (the fast path -- the CDP colour field is hex-constrained, so this
// is all that normally runs); anything else valid (a named colour, hsl(), ...)
// is resolved through the browser so the contrast guard works whatever format a
// trait arrives in, not just hex. Returns null for an unrecognised value. The
// DOM probe only runs for the exotic formats, never the hex common case.
//
export function resolveColor(value: unknown, document: Document): Rgb | null {
  const rgb = parseColor(value);
  if (rgb) {
    return rgb;
  }
  if (
    typeof value !== "string" ||
    !value.trim() ||
    !document ||
    !document.createElement
  ) {
    return null;
  }
  try {
    const probe = document.createElement("span");
    probe.style.color = value; // an invalid value leaves style.color === ''
    if (!probe.style.color) {
      return null;
    }
    probe.style.display = "none";
    const host = document.body || document.documentElement;
    host.appendChild(probe);
    const view =
      document.defaultView || (typeof window !== "undefined" ? window : null);
    const computed = view ? view.getComputedStyle(probe).color : "";
    host.removeChild(probe);
    return parseColor(computed);
  } catch {
    return null;
  }
}

// Keep a publisher's Pixie icon colour legible on whatever tile it sits on
// (default white): if the icon/tile contrast is too low, nudge the icon AWAY
// from the tile's luminance -- darker on a light tile, lighter on a dark one --
// until it reads. Returns the colour as-is when it already contrasts, an rgb()
// string when adjusted, or null for an unparseable icon colour so the caller
// leaves the CSS default in place.
export function accentForTile(
  accentValue: string,
  tileValue: string,
  document: Document,
): string | null {
  const accent = resolveColor(accentValue, document);
  if (!accent) {
    return null;
  }
  const tile = resolveColor(tileValue, document) || [255, 255, 255];
  const luminance = function (rgb: Rgb) {
    const lin = rgb.map(function (channel) {
      const c = channel / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  };
  const contrast = function (a: Rgb, b: Rgb) {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  };
  // Min icon-vs-tile contrast for a small glyph -- WCAG 1.4.11 (non-text
  // contrast) 3:1. Below it the icon is nudged away from the tile's luminance.
  const minContrast = 3.0;
  if (contrast(accent, tile) >= minContrast) {
    return accentValue.trim();
  }
  const tileIsLight = luminance(tile) > 0.5;
  let adjusted = accent.slice();
  for (let i = 0; i < 24 && contrast(adjusted, tile) < minContrast; i++) {
    adjusted = adjusted.map(function (channel) {
      return tileIsLight
        ? Math.max(0, Math.round(channel * 0.9)) // darken toward black
        : Math.min(255, Math.round(channel + (255 - channel) * 0.12)); // lighten toward white
    });
  }
  return `rgb(${adjusted[0]}, ${adjusted[1]}, ${adjusted[2]})`;
}
